import { Expose } from 'class-transformer';

const categoryMap = {
	'Special Instructions': ['Special Instructions'],
	'Auxiliary Devices and Services': ['Amplified Device', 'Hearing Aid', 'TTY/TTD/Relay Equipment', 'Contact Lenses', 'Glasses', 'Magnify Glass', 'Braille', 'Interpreter Needed - Foreign Language', 'Interpreter Needed - Sign Language', 'Dental Appliance', 'Prosthetic Eye', 'Limb Prosthesis', 'Cane/Crutch', 'Electric Wheelchair', 'Reading Only', 'Manual Wheelchair', 'Walker', 'Jewelry', 'Other:'],
	'Devices and Treatments': ['BiPap', 'Bladder (Foley) Catheter', 'CPAP', 'Enteral Feeding', 'Internal Defibrillator', 'IV(Parenteral/IV Feeding)', 'Hemodialysis', 'Ventilator', 'Chest Tube', 'Lifevest', 'Colostomy', 'Nebulizer Therapy', 'O2 at____ L/Min by Nasal Canula/Mask()', 'Ostomy', 'Pacemaker', 'PiCC Line', 'Suprapubic Catheter', 'Condom Catheter', 'Peritoneal dialysis', 'Suction', 'Cancer Treatments', 'Urostomy', 'Dialysis Shunt', 'TPN', 'Other:', 'Urinary Toileting Program', 'Bowel Toileting Program', 'No Active Medication', 'No Known Diagnoses', 'External Catheter', 'Tracheostomy', 'Drains', 'Ileostomy'],
	'Isolation / Precautions': ['Isolation - Active Infectious Disease', 'Airborne Precautions', 'Chemo Precautions', 'Contact Isolation Precautions', 'Droplet Precautions', 'Reverse Isolation'],
	'Special Care': ['Hospice', 'Respite Care', 'Palliative Care', 'ACO', 'Bundled Payment Program:', 'Goal to return home (alone)', 'Goal to return home with assist', 'LTC, AL/IL', 'Unknown', 'Other:'],
	Therapies: ['Physical Therapy (PT)', 'Occupational Therapy (OT)', 'Speech Therapy (ST)'],
	'Risk Alerts': ['Anticoagulation', 'Aspiration', 'Falls', 'Harm to self or others', 'Limited/non-weight bearing:', 'Accidents', 'Dementia', 'Infections', 'Nutrition', 'Quality of Life', 'Social Issues / Lack of Support', 'May attempt exit', 'Needs meds crushed', 'Pressure Ulcer(s)', 'Restraints', 'Seizures', 'Bowel/Bladder', 'Depression', 'Medications/Polypharmacy', 'Pain', 'Repeat Hospitalizations', 'Surgical Complications', 'Swallowing precautions', 'Cultural Issues', 'Health Literacy/Knowledge', 'Non-Compliance', 'Physical Function', 'Side Effects of Active Chemo or Radiation', 'Therapy', 'Weight Change'],
	'Impairments - Musculoskeletal': ['Amputation', 'Contractures', 'Paralysis', 'Other:'],
	'Imairments - General': ['Cognitive', 'Hearing', 'Sensation', 'Speech', 'Vision', 'Other:', 'Comatose'],
	Communication: ['Verbal', 'Sign Language', 'Computer', 'Written', 'Communication Board', 'Picture Book'],
	'Smoking Status': ['Smoker', 'Start Date:', 'End Date:']
};

const otherMap = {
	18: 'Auxiliary Devices and Services',
	47: 'Devices and Treatments',
	62: 'Risk Alerts',
	67: 'Impairments - Musculoskeletal',
	74: 'Imairments - General',
	159: 'Special Care'
};
/**
 *
 */

/**
 * Represents a care profile question response from the PointClickCare API
 *
 * SimpliScreen maps the care profile question to the following string for embedding in the RAG:
 * questionText: {questionText}, value: {value}
 *
 * @see https://connect2.pointclickcare.com/api/public/preview1/orgs/{orgUuid}/care-profiles?patientId={patientId}&questionIds={questionIds}
 * questionIds is a optional parameter and if not provided, all questions for the patient will be returned.
 */
export class PccCareProfileQuestion {
	/**
	 * PointClickCare's unique identifier for the care profile questions
	 */
	questionId: number;

	/**
	 * Represents the displayed question text in the care profile
	 */
	questionText: string;

	/**
	 * PointClickCare's unique identifier for the care profile questions that points back to the questionId
	 * This field is optional and only present for sub-questions
	 */
	parentQuestionId?: number;

	/**
	 * PointClickCare's unique identifier for a patient within the organization.
	 *
	 * When a patient is admitted to a facility within an organization, they are assigned a patientId which is unique to their stay within that facility. If the same patient were to get admitted to another facility within the same organization in the future, they would be assigned a new patientId. The patientId however is unique at the organization level. That is, no two patients across different facilities within the organization will have the same patientId.
	 *
	 * This identifier is crucial for tracking patient information and care but must not be displayed in any user interface due to legal privacy requirements.
	 */
	patientId: number;

	/**
	 * PointClickCare's text or response displayed for care profile questions; blank, N, Y or text
	 */
	value: string | number;

	/**
	 * Children of the care profile question
	 */
	children?: PccCareProfileQuestion[] = [];

	/**
	 * Parent of the care profile question
	 */
	parent?: PccCareProfileQuestion;

	/**
	 * Represents the details of the care profile question.
	 *
	 * SimpliScreen maps the care profile question to the following string for embedding in the RAG:
	 * questionText: {questionText}, value: {value}
	 */
	details?: string;

	/**
	 *
	 * The category of the care profile question.
	 */
	category?: string;

	constructor(partial?: Partial<PccCareProfileQuestion>) {
		Object.assign(this, partial);
		if (!this.parentQuestionId) this.category = otherMap[this.questionId] || Object.keys(categoryMap).find((category) => categoryMap[category].includes(this.questionText)) || 'Additional Information';
	}

	@Expose({ name: 'details' })
	getDetails?(prefix = '') {
		const details = [];
		if (this.questionText) details.push(`Question Text: ${this.questionText}`);
		if (this.value) details.push(`Value: ${this.value}`);
		return `${prefix}${details.join(', ')}${this.children.map((child) => child.getDetails(`\n${prefix}\t`)).join('')}`;
	}
}

export class PccCareProfile {
	questions: PccCareProfileQuestion[];

	heirarchy: PccCareProfileQuestion[];

	/**
	 * Represents the details of the care profile.
	 */
	details?: string;

	constructor(questions: PccCareProfileQuestion[]) {
		const flatCareProfile = questions.map((question) => new PccCareProfileQuestion(question));
		this.heirarchy = [];
		for (const question of flatCareProfile) {
			if (question.parentQuestionId) {
				const parent = flatCareProfile.find((q) => q.questionId === question.parentQuestionId);
				parent?.children?.push(question);
				question.category = parent?.category;
			} else {
				this.heirarchy.push(question);
			}
		}
		this.questions = this.heirarchy.flatMap((question) => [question, ...question.children || []]);
	}

	@Expose({ name: 'details' })
	getDetails() {
		return `Care Profile Answers:\n${this.heirarchy.sort((a, b) => a.questionId - b.questionId).map((question) => question.getDetails()).join('\n')}`;
	}
}
